import React, { useEffect } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { useRouter, useLocalSearchParams, Stack } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import Button from '../../src/components/Button';
import { useAuth } from '../../src/context/AuthContext';
import { useTheme } from '../../src/context/ThemeContext';
import { spacing, fontSize, fontWeight } from '../../src/utils/theme';
import ContentContainer from '../../src/components/ContentContainer';
import type { ColorTheme } from '../../src/utils/theme';

type IoniconsName = React.ComponentProps<typeof Ionicons>['name'];

const REASONS: Record<string, { title: string; description: string; icon: IoniconsName }> = {
  no_payment_method: {
    title: 'Payment Method Required',
    description: 'Please add a payment method before starting a wash.',
    icon: 'card-outline',
  },
  owner_not_activated: {
    title: 'Location Unavailable',
    description: 'This location is not currently accepting purchases. Please try again later.',
    icon: 'storefront-outline',
  },
  controller_offline: {
    title: 'Equipment Offline',
    description: 'The wash equipment is not responding. It may be powered off or experiencing connectivity issues.',
    icon: 'wifi-outline',
  },
  bay_deactivated: {
    title: 'Bay Unavailable',
    description: 'This bay is currently turned off. Please try another bay.',
    icon: 'power-outline',
  },
  stripe_not_onboarded: {
    title: 'Payments Not Set Up',
    description: 'This location cannot accept payments yet. Please contact the location owner.',
    icon: 'card-outline',
  },
  bay_active: {
    title: 'Bay In Use',
    description: 'This bay already has an active wash session. Please wait or try another bay.',
    icon: 'time-outline',
  },
  payment_failed: {
    title: 'Payment Failed',
    description: 'Your payment could not be processed. Please check your payment method and try again.',
    icon: 'close-circle-outline',
  },
  bundle_expired: {
    title: 'Bundle Expired',
    description: 'Your wash bundle has expired. Please purchase a new bundle to continue.',
    icon: 'calendar-outline',
  },
  subscription_limit: {
    title: 'Wash Limit Reached',
    description: 'You have reached your daily or monthly wash limit for this subscription.',
    icon: 'speedometer-outline',
  },
  fleet_inactive: {
    title: 'Fleet Account Inactive',
    description: 'Your fleet account is not active. Please contact your fleet administrator.',
    icon: 'business-outline',
  },
  generic: {
    title: 'Something Went Wrong',
    description: 'An unexpected error occurred. Please try again.',
    icon: 'alert-circle-outline',
  },
};

/** WashFailedScreen - displays contextual error message when a wash cannot start. */
export default function WashFailedScreen() {
  const { colors } = useTheme();
  const styles = useStyles(colors);
  const router = useRouter();
  const { isAuthenticated, isLoading, role } = useAuth();
  const { reason, message } = useLocalSearchParams<{ reason?: string; message?: string }>();

  useEffect(() => {
    if (!isLoading && !isAuthenticated) router.replace('/');
  }, [isLoading, isAuthenticated]);

  const info = REASONS[reason || ''] || REASONS.generic;
  // Owners can land here when they test-purchase a wash from their own QR; send
  // them back to the owner dashboard, not the customer scan screen.
  const homeRoute = role === 'owner' ? '/owner' : role === 'admin' ? '/admin' : '/user';

  return (
    <>
      <Stack.Screen options={{ title: 'Wash Failed' }} />
      <View style={styles.container}>
        <ContentContainer maxWidth={480} style={{ alignItems: 'center' }}>
          <Ionicons name={info.icon} size={80} color={colors.danger} />
          <Text style={styles.title}>{info.title}</Text>
          <Text style={styles.subtitle}>{message || info.description}</Text>
          <Button
            title="Back to Home"
            onPress={() => router.replace(homeRoute)}
            style={styles.btn}
          />
        </ContentContainer>
      </View>
    </>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  container: { flex: 1, justifyContent: 'center', alignItems: 'center', padding: spacing.xl, backgroundColor: c.background },
  title: { fontSize: fontSize.xxl, fontWeight: fontWeight.bold, color: c.text, marginTop: spacing.lg, textAlign: 'center' },
  subtitle: { fontSize: fontSize.md, color: c.textSecondary, marginTop: spacing.sm, textAlign: 'center', lineHeight: 22 },
  btn: { marginTop: spacing.xl, minWidth: 200 },
});
const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
